import {Component, forwardRef, Input, OnInit, Output, EventEmitter} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validators, Validator, AbstractControl, ValidationErrors} from '@angular/forms';
import {MatDatepickerInputEvent} from '@angular/material/datepicker';

interface DateValidationMessages {
  required?: string;
  matDatepickerMin?: string;
  matDatepickerMax?: string;
  matDatepickerParse?: string;
}

@Component({
  selector: 'greydom-date-picker',
  templateUrl: './element-date-picker-component.component.html',
  styleUrls: ['element-date-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ElementDatePickerComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ElementDatePickerComponent),
      multi: true
    }
  ]
})
export class ElementDatePickerComponent implements OnInit, ControlValueAccessor, Validator {
  readonly defaultErrorMessages: Partial<DateValidationMessages> = {
    required: 'This field is required',
    matDatepickerMin: 'Date cannot be before start date',
    matDatepickerMax: 'Date cannot be after end date',
    matDatepickerParse: 'Invalid date format',
  };

  dateForm: FormGroup;

  @Input() hint = '';
  @Input() label = 'Select Date';
  @Input() qaAttr: string;
  @Input() pickerId: string;
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Input() required = false;
  @Input() placeholder = '';
  @Input() value: Date | null = null;
  @Input() tooltip: string;
  @Input() showInfoIcon = true;
  @Input() customCssClasses: Array<string> = [];
  @Input() customErrorMessages?: DateValidationMessages;
  @Input() markAsDirtyOnEdit = false;

  @Output() valueChange: EventEmitter<Date> = new EventEmitter<Date>();

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private formBuilder: FormBuilder) {}

  get errorMessages(): DateValidationMessages {
    return {...this.defaultErrorMessages, ...this.customErrorMessages};
  }

  ngOnInit(): void {
    this.initFormGroup();
  }

  initFormGroup(): void {
    const validators = [];

    if (this.required) {
      validators.push(Validators.required);
    }

    this.dateForm = this.formBuilder.group({
      date: [this.value, validators]
    });
  }

  writeValue(value: any): void {
    if (this.dateForm) {
      this.value = value;
      this.dateForm.patchValue({date: value}, {emitEvent: false});
      this.markControlTouchedAndDirty();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.dateForm.disable({emitEvent: false});
    } else {
      this.dateForm.enable({emitEvent: false});
      this.markControlTouchedAndDirty();
    }
  }

  getErrorMessage(): string {
    const control = this.dateForm?.get('date');
    if (!control?.errors) {
      return '';
    }

    const errorKey = Object.keys(control.errors)[0] as keyof DateValidationMessages;
    return this.errorMessages[errorKey] || '';
  }

  onDateChange(event: MatDatepickerInputEvent<Date>): void {
    const value: Date = event.value;
    this.value = value;
    this.dateForm.patchValue({date: value}, {emitEvent: false});
    this.onChange(value);
    this.onTouched();
    this.valueChange.emit(value);
    this.markControlTouchedAndDirty();
  }

  private markControlTouchedAndDirty(): void {
    const dateControl = this.dateForm?.get('date');
    if (!dateControl) {
      return;
    }

    if (this.markAsDirtyOnEdit || dateControl.errors) {
      dateControl.markAsTouched();
      dateControl.markAsDirty();
    }

    dateControl.updateValueAndValidity();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.dateForm?.get('date')?.errors || null;
  }
}
